import {PartialDeep, ValueOf} from 'type-fest'
import {FontName} from '../types'

export type Font = 'body' | 'heading' | 'ui'

interface FontSpec {
  fallbacks: readonly string[]
  weights: {light: number; regular: number; bold: number}
  scales: {size: number; height: number; margin: number}
  suitableForUi: boolean
  companion: FontName | null
}

export type Weight = keyof FontSpec['weights']

export const fontSpecs = {
  Merriweather: {
    fallbacks: ['serif'],
    weights: {light: 300, regular: 300, bold: 600},
    scales: {size: 1, height: 1, margin: 1},
    suitableForUi: false,
    companion: 'Merriweather Sans',
  },
  'Merriweather Sans': {
    fallbacks: ['sans-serif'],
    weights: {light: 300, regular: 300, bold: 600},
    scales: {size: 1, height: 1, margin: 1},
    suitableForUi: true,
    companion: null,
  },
  'Open Sans': {
    fallbacks: ['sans-serif'],
    weights: {light: 300, regular: 400, bold: 600},
    scales: {size: 1, height: 1, margin: 1},
    suitableForUi: true,
    companion: null,
  },
} as const satisfies {
  [k in FontName]: FontSpec
}

const deriveUiFont = (..._names: Array<FontName | undefined>) => {
  const names = _names.filter(name => name && name in fontSpecs) as FontName[]
  for (const name of names) {
    if (fontSpecs[name].suitableForUi) {
      return name
    }
  }
  for (const name of names) {
    const {companion} = fontSpecs[name]
    if (companion && fontSpecs[companion].suitableForUi) {
      return companion
    }
  }
}

export const cssVars = {
  fontFamily: '--evergreen-font-family',
  weights: {
    light: '--evergreen-font-weight-light',
    regular: '--evergreen-font-weight-regular',
    bold: '--evergreen-font-weight-bold',
  },
  scales: {
    size: '--evergreen-font-scale-size',
    height: '--evergreen-font-scale-height',
    margin: '--evergreen-font-scale-margin',
  },
} as const

type CssVar =
  | Extract<ValueOf<typeof cssVars>, string>
  | ValueOf<(typeof cssVars)['weights']>
  | ValueOf<(typeof cssVars)['scales']>

export const getFonts = (
  theme: PartialDeep<{
    branding: {bodyFont: FontName; headingFont: FontName}
  }>,
) => {
  const {branding} = theme
  const baseFonts: {[k in Font]: FontName} = {
    ui: deriveUiFont(branding?.bodyFont, branding?.headingFont) || 'Open Sans',
    body: branding?.bodyFont || 'Open Sans',
    heading: branding?.headingFont || 'Open Sans',
  }

  const knownFonts = Object.entries(fontSpecs).reduce(
    (kfs, [_name, {fallbacks, weights, scales}]) => {
      const name = _name as FontName
      kfs[name] = {
        [cssVars.fontFamily]: [name, ...fallbacks].join(', '),
        // These need to be strings otherwise styled-components will append px
        [cssVars.weights.light]: `${weights.light}`,
        [cssVars.weights.regular]: `${weights.regular}`,
        [cssVars.weights.bold]: `${weights.bold}`,
        [cssVars.scales.size]: `${scales.size}`,
        [cssVars.scales.height]: `${scales.height}`,
        [cssVars.scales.margin]: `${scales.margin}`,
      }
      return kfs
    },
    {} as {[k in FontName]: {[v in CssVar]: string}},
  )

  const fonts = Object.entries(baseFonts).reduce(
    (fvs, [_font, fontName]) => {
      const font = _font as Font
      fvs[font] = {
        ...knownFonts[fontName],
        fontFamily: `var(${cssVars.fontFamily})`,
      }
      return fvs
    },
    {} as {
      [k in Font]: ValueOf<typeof knownFonts> & {fontFamily: string}
    },
  )

  // compat
  const fontWeights = Object.fromEntries(
    Object.entries(cssVars.weights).map(([w, v]) => [w, `var(${v})`]),
  ) as {[k in keyof typeof cssVars.weights]: string}

  return {baseFonts, fonts, fontWeights}
}
